import { inject, Injectable } from '@angular/core';
import { ICustomer } from '../models/customer.model';
import { AccountService } from './account.service';
import { CurrentDataService } from './current-data-service';
import { map, mergeMap, take } from 'rxjs/operators';
import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, OperatorFunction } from 'rxjs';
import { ResourceUtil } from '../app/shared/util/resource-util';
import { environment } from '../environments/environment';
import { AuthorityEnum } from '../models/enums/authority.enum';

type HttpOption = {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  observe: 'response';
  context?: HttpContext;
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class ApiProviderService {
  readonly #currentDataService = inject(CurrentDataService);
  readonly #accountService = inject(AccountService);
  readonly #httpClient = inject(HttpClient);

  private PUBLIC_API = environment.urlApi + 'api/open';

  public get<T>(
    fragment: string,
    options?:
      | {
          responseType?: 'text';
        }
      | HttpParams
      | HttpOption
  ): Observable<HttpResponse<T>> {
    const innerOption = this.mapOptions(options);
    return this.#currentDataService.getCustomer().pipe(
      take(1),
      this.getUrl(fragment),
      mergeMap((url: string) => this.#httpClient.get<T>(url, innerOption))
    );
  }

  public download<T>(fragment: string, forcedFilename?: string): void {
    this.#currentDataService
      .getCustomer()
      .pipe(
        take(1),
        this.getUrl(fragment),
        mergeMap((url: string) =>
          this.#httpClient.get<T>(url, {
            observe: 'response',
            responseType: 'blob' as 'json',
          })
        )
      )
      .subscribe((response: any) => {
        const dataType = response.type;
        const binaryData = [];
        binaryData.push(response.body);
        const downloadLink = document.createElement('a');
        downloadLink.setAttribute('target', '_blank');
        const objectUrl = URL.createObjectURL(new Blob(binaryData, { type: dataType }));
        downloadLink.href = objectUrl;
        if (!forcedFilename) {
          const content = response.headers.get('Content-Disposition');
          if (content) {
            forcedFilename = this.parseFileName(content, 'filename');
          }
        }
        if (forcedFilename) {
          downloadLink.setAttribute('download', forcedFilename);
        }
        document.body.appendChild(downloadLink);
        downloadLink.click();
        URL.revokeObjectURL(objectUrl);
      });
  }

  public browserPdf<T>(
    fragment: string,
    forcedFilename?: string
  ): Observable<{
    blob: Blob;
    filename: string | undefined;
    title: string | undefined;
  }> {
    return this.#currentDataService.getCustomer().pipe(
      take(1),
      this.getUrl(fragment),
      mergeMap((url: string) =>
        this.#httpClient.get<T>(url, {
          observe: 'response',
          responseType: 'blob' as 'json',
        })
      ),
      map((response: any) => {
        const binaryData = [];
        binaryData.push(response.body);
        const content = response.headers.get('Content-Disposition');
        let forcedName;
        if (content) {
          forcedName = this.parseFileName(content, 'name');
          forcedFilename = this.parseFileName(content, 'filename');
        }

        return { blob: response.body as Blob, title: forcedName, filename: forcedFilename };
      })
    );
  }

  private parseFileName(header: string, search: 'filename' | 'name', def?: string): string | undefined {
    if (!header) {
      return def;
    }
    let data: string;
    if (search === 'name') {
      data = header.split(';')[1].trim().split('=')[1];
    } else {
      data = header.split(';')[2].trim().split('=')[1];
    }
    return decodeURI(data.replace(/"/g, '')); // Note that non-English content please add url encoding on the server side
  }

  public post<T>(fragment: string, body: any, options: HttpOption = { observe: 'response' }): Observable<HttpResponse<T>> {
    return this.#currentDataService.getCustomer().pipe(
      take(1),
      this.getUrl(fragment),
      mergeMap((url: string) => this.#httpClient.post<T>(url, body, options))
    );
  }

  public delete<T>(fragment: string): Observable<HttpResponse<T>> {
    return this.#currentDataService.getCustomer().pipe(
      take(1),
      this.getUrl(fragment),
      mergeMap((url: string) => this.#httpClient.delete<T>(url, { observe: 'response' }))
    );
  }

  public put<T>(fragment: string, body: any): Observable<HttpResponse<T>> {
    return this.#currentDataService.getCustomer().pipe(
      take(1),
      this.getUrl(fragment),
      mergeMap((url: string) => this.#httpClient.put<T>(url, body, { observe: 'response' }))
    );
  }

  public patch<T>(fragment: string, body: any): Observable<HttpResponse<T>> {
    return this.#currentDataService.getCustomer().pipe(
      take(1),
      this.getUrl(fragment),
      mergeMap((url: string) => this.#httpClient.patch<T>(url, body, { observe: 'response' }))
    );
  }

  // /!\ PUBLIC URL
  public publicGet<T>(fragment: string, options?: any): Observable<HttpResponse<T>> {
    const innerOption = this.mapOptions(options);
    return this.#httpClient.get<T>(this.PUBLIC_API + fragment, innerOption);
  }

  // /!\ PUBLIC URL
  public publicPost<T>(fragment: string, entity?: any): Observable<HttpResponse<T>> {
    return this.#httpClient.post<T>(this.PUBLIC_API + fragment, entity, { observe: 'response' });
  }

  public publicDelete<T>(fragment: string): Observable<HttpResponse<T>> {
    return this.#httpClient.delete<T>(this.PUBLIC_API + fragment, { observe: 'response' });
  }

  public configurationPut<T>(fragment: string, entity?: T): Observable<HttpResponse<T>> {
    return this.#currentDataService.getCustomer().pipe(
      take(1),
      this.getUrl(fragment),
      mergeMap((url: string) => this.#httpClient.put<T>(url, entity, { observe: 'response' }))
    );
  }

  private getUrl(fragment: string): OperatorFunction<ICustomer | undefined, string> {
    return map((customer: ICustomer | undefined) => this.getUrlPath(fragment, customer!));
  }

  public getUrlPath(fragment: string, customer: ICustomer): string {
    if (this.#accountService.hasAnyAuthority(AuthorityEnum.ADMIN)) {
      return ResourceUtil.asAdminURL(fragment, customer);
    }
    if (this.#accountService.hasAnyAuthority(AuthorityEnum.MODERATOR)) {
      return ResourceUtil.asModeratorURL(fragment, customer);
    }
    if (this.#accountService.hasAnyAuthority(AuthorityEnum.USER)) {
      return ResourceUtil.asUserURL(fragment, customer);
    }
    return this.PUBLIC_API.concat(fragment);
  }

  private mapOptions(extOption?: any | HttpParams): {
    observe: 'response';
    params?:
      | HttpParams
      | {
          [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
        };
  } {
    const defaultOption: {
      observe: 'response';
      params?:
        | HttpParams
        | {
            [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
          };
    } = { observe: 'response' };
    const innerOption = defaultOption;
    if (extOption && extOption instanceof HttpParams) {
      innerOption.params = extOption;
      return innerOption;
    } else if (extOption?.observe || extOption?.params) {
      if (extOption?.observe) {
        innerOption.observe = extOption.observe;
      }
      innerOption.params = extOption?.params;
      return innerOption;
    } else if (extOption) {
      return extOption as {
        observe: 'response';
        params?:
          | HttpParams
          | {
              [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
            };
      };
    }
    return innerOption;
  }
}
